/**
 * Canonical SDK field projection for `useGetProposal` on the proposal-detail
 * page (v2 SDK manifest entry).
 *
 * Used in two places that MUST stay in lockstep:
 *   1. Server-side prefetch in `app/[locale]/launchpad/[proposalId]/page.tsx`
 *      via `buildProposalDetailPrefetch`.
 *   2. Client-side `useGetProposal({ pubkey, fields: [...] })` call inside
 *      `<ProposalDetailPage>`.
 *
 * The SDK cache key is `[path, params, fields]`, so any drift between the
 * server prefetch and the client hook produces distinct cache entries and the
 * client refetches on mount — defeating the SSR hydration optimisation.
 *
 * Projection includes both `backers` (recent backers — capped server-side)
 * and `viewerShareBalance` (the viewer's current redeemable share-mint
 * balance D6; the backend omits this field when no session is present).
 * The proposer rollup carries `proposalsCreated` + `topMarketVolume` so
 * the creator card has real data without a second hook call.
 *
 * Unlike the fixed market DTO hooks, proposal detail remains a projected
 * endpoint and must keep its client/SSR field arrays aligned.
 */
export const PROPOSAL_DETAIL_FIELDS = [
  'marketType',
  'partnerCreatorId',
  'associatedCreator',
  'pubkey',
  'proposalId',
  'title',
  'description',
  'status',
  'coverImageUrl',
  'coverImageNsfw',
  'totalFunded',
  'numBackers',
  'backerCount',
  'category',
  'location',
  'marketConfig',
  'proposer',
  'createdAt',
  'expiresAt',
  'debateSpreadOk',
  'numOutcomes',
  'outcomes',
  'graduatedMarketPubkey',
  'proposalScore',
  'upvotes',
  'downvotes',
  'netVotes',
  'myVote',
  'backers',
  'viewerShareBalance',
] as const;

/**
 * Lean field set for hover-prefetch (intent-driven) on proposal rows — the
 * SearchModal's `ProposalRowSearchItem`, the launchpad feed row, and any
 * future "hover to warm proposal detail" surface.
 *
 * Hover prefetch is for first-paint chrome only: enough to render the title,
 * cover, status, funded amount, backer count, and proposer card. The actual
 * detail page still requests the full `PROPOSAL_DETAIL_FIELDS` set — the
 * cache-key mismatch is intentional. The click→detail still pays a full fetch,
 * but the hover request is cheap and lands fast for the perceived-latency win.
 */
export const PROPOSAL_DETAIL_PREFETCH_FIELDS = [
  'marketType',
  'partnerCreatorId',
  'associatedCreator',
  'pubkey',
  'proposalId',
  'title',
  'status',
  'coverImageUrl',
  'coverImageNsfw',
  'totalFunded',
  'numBackers',
  'category',
  'proposer',
] as const;
